import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { fullWidth, scale as initialScale } from '@components/frame';
import {
  getNCFileFromVersions,
  ncFileStatuses,
  isPhone,
  navigationRef,
  calculateTimeDifferenceInPercentage,
  isPhoneNative,
} from '../../../../../../../../helpers';
import { getBoxShadowStyle } from '@styles/shadow';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { selectShouldShowGlobalFullNCFileButton, setShouldShowGlobalFullNCFileButton } from '@reducers/machinesV2';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { NowSubScreen } from '@v2/pages/machines/common/enums';
import { useDocumentsNavigation } from '@v2/pages/common/hooks/useDocumentsNavigation';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { ncFileStatisticsTimes } from '@reducers/statistics';
import ProductivityTimeline from '@components/common/ProductivityTimeline';
import { selectSingleBatch } from '@reducers/batch';
import { selectIsWithERP } from '@reducers/membership';
import { selectNcFileData } from '@reducers/details';
import { buildVersionLabel } from '@components/ncFileVersions/helpers/buildVersionLabel';
import type { NCFileBlockProps } from '@components/machines/components/detailsComponents/activeMachine/components/NCFile/types';

export interface UseManageNCFileParams extends Pick<NCFileBlockProps, 'C' | 'selectedItem' | 'scale'> {}

export const useManageNCFile = ({ selectedItem, scale = initialScale, C }: UseManageNCFileParams) => {
  const {
    machineStatus: { ncFile: _ncFile, ncFileNumber, ncFileId: _ncFileId, ncFileStatus: _ncFileStatus, ncFileVersion, clampingDetails },
    machineStatus,
    machineId,
  } = selectedItem;

  const dispatch = useAppDispatch();
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const isFocused = useIsFocused();
  const { safeIsMachinesScreenCheck, safeIsNowSubScreenCheck, safeIsDetailsScreen } = useNavigationCheck();
  const route = navigationRef?.current?.getCurrentRoute();
  const perspective = route?.params?.perspective;
  const isNcFilePerspective = perspective === Perspective.NC_FILE;
  const isBatchPerspective = perspective === Perspective.BATCH;
  const { IS_NARROW_WEB } = useDeviceSize();
  const { handleNowScreenNavigation } = useDocumentsNavigation();

  const batch = useSelector(selectSingleBatch);
  const isWithERP = useSelector(selectIsWithERP);
  const ncFileDataObject = useSelector(selectNcFileData);
  const shouldShowGlobalFullNCFileButton = useSelector(selectShouldShowGlobalFullNCFileButton);

  const ncFileData = ncFileDataObject.data;

  const [shouldShowFullNCFileButtonInternal, setShouldShowFullNCFileButtonInternal] = useState(true);
  const [containerCoordinates, setContainerCoordinates] = useState(null);
  const [showFullNCFileButtonWidth, setShowFullNCFileButtonWidth] = useState(0);
  const [ncFileName, setNcFileName] = useState(null);
  const [ncFileTimes, setNcFileTimes] = useState(null);
  const [optimisticPiecesPerCycle, setOptimisticPiecesPerCycle] = useState(null);

  const scrollContainerRef = useRef(null);

  const version = getNCFileFromVersions(safeIsMachinesScreenCheck ? machineStatus?.versions : ncFileData?.versions);
  const ncFile = !safeIsMachinesScreenCheck ? ncFileData?.ncFileName : _ncFile;
  const ncFileId = !safeIsMachinesScreenCheck ? ncFileData?.ncFileId : _ncFileId;
  const ncFileStatus = !safeIsMachinesScreenCheck ? ncFileData?.ncFileStatus : _ncFileStatus;
  const shouldProvideBatchIdDuringLinking = (safeIsNowSubScreenCheck || isBatchPerspective) && batch?.ncFileId === ncFileId;

  const toVersions = () => {
    navigateToMainDetailsScreen({
      mainScreen: NCFileDetailsScreenName,
      subScreen: NCFileDetailsScreen.NC_FILE,
      levelSubScreen: LevelSubScreen.VERSIONS,
      params: {
        perspective: Perspective.NC_FILE,
        ncFileId,
        machineName: ncFileData?.firstRanByMachineName,
      },
    });
  };

  const versionNumber = useMemo(() => {
    const versionTextBasedOnNcDataVersion = buildVersionLabel(version);

    if (!safeIsMachinesScreenCheck) return versionTextBasedOnNcDataVersion;
    return ncFileNumber || ncFileVersion || versionTextBasedOnNcDataVersion;
  }, [version, safeIsMachinesScreenCheck, ncFileNumber, ncFileVersion]);

  const ncFileStatusMessage = {
    [ncFileStatuses.Unknown]: 'Not Linked',
    [ncFileStatuses.Linked]: 'Linked',
    [ncFileStatuses.Change]: 'Change',
    [ncFileStatuses.Ignored]: 'Ignore',
    [ncFileStatuses.Setup]: 'Setup',
  };

  const roundTopConfig = {
    borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
    borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
  };

  const onNCFileNameClick = () => {
    navigateToMainDetailsScreen({
      mainScreen: NCFileDetailsScreenName,
      subScreen: NCFileDetailsScreen.NC_FILE,
      params: {
        perspective: Perspective.NC_FILE,
        ncFileId,
        ncFileName: ncFile,
        ncFileStatus,
      },
    });
  };

  const onNCFileImageClick = (selectedImageKey) => {
    if (safeIsNowSubScreenCheck) {
      handleNowScreenNavigation({ nowSubScreen: NowSubScreen.NC_FILE, params: { selectedImageKey } });
      return;
    }
    navigateToMainDetailsScreen({
      mainScreen: NCFileDetailsScreenName,
      subScreen: NCFileDetailsScreen.NC_FILE,
      levelSubScreen: LevelSubScreen.DOCUMENTS,
      params: {
        perspective: Perspective.NC_FILE,
        ncFileId,
        ncFileName: ncFile,
        ncFileStatus,
        selectedImageKey,
      },
    });
  };

  const shouldHandleProductiveTimes = safeIsDetailsScreen && ncFileStatus === ncFileStatuses.Linked;

  useEffect(() => {
    const listener = () => {
      scrollContainerRef?.current?.measureInWindow((x, y, width, height) => {
        setContainerCoordinates({ x, width });
      });
    };

    listener();
  }, [scrollContainerRef, fullWidth]);

  useFocusEffect(
    useCallback(() => {
      return () => {
        setShouldShowFullNCFileButtonInternal(false);
        dispatch(setShouldShowGlobalFullNCFileButton(false));
      };
    }, [])
  );

  useFocusEffect(
    useCallback(() => {
      setShouldShowFullNCFileButtonInternal(!IS_NARROW_WEB);
    }, [IS_NARROW_WEB])
  );

  useFocusEffect(
    useCallback(() => {
      if (!shouldHandleProductiveTimes) return;

      (async () => {
        if (ncFileId) {
          const data = await dispatch(
            ncFileStatisticsTimes({
              ncFileId,
            })
          );
          if (data?.statisticsOverall) {
            setNcFileTimes(data.statisticsOverall);
          }
        }
      })();
    }, [ncFileId, shouldHandleProductiveTimes])
  );

  const leftForShowFullNCFileButton = useMemo(() => {
    if (!containerCoordinates || !showFullNCFileButtonWidth) return null;
    return (safeIsDetailsScreen && !isPhoneNative ? 0 : containerCoordinates?.x) + containerCoordinates?.width / 2 - showFullNCFileButtonWidth / 2;
  }, [containerCoordinates?.x, containerCoordinates?.width, showFullNCFileButtonWidth, isPhoneNative]);

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  const onTimePress = () => {
    navigateToMainDetailsScreen({
      mainScreen: NCFileDetailsScreenName,
      subScreen: NCFileDetailsScreen.NC_FILE,
      levelSubScreen: LevelSubScreen.TIMES,
      params: {
        perspective: Perspective.NC_FILE,
        ncFileId,
        ncFileName: ncFile,
        ncFileStatus,
      },
    });
  };

  const productiveTimeBlockConfig = (() => {
    if (!shouldHandleProductiveTimes || !ncFileTimes) return [];

    const scheduledTotal = ncFileTimes?.averageActiveSecs + ncFileTimes?.averageChangeSecs;
    const idealTotal = ncFileTimes?.averageIdealActiveSecs + ncFileTimes?.averageIdealChangeSecs;
    const paidTotal = ncFileTimes?.averagePaidActiveSecs + ncFileTimes?.averagePaidChangeSecs;

    const computedIdealTimePercentage = idealTotal ? calculateTimeDifferenceInPercentage(idealTotal, scheduledTotal) : null;
    const computedPaidTimePercentage = paidTotal ? calculateTimeDifferenceInPercentage(paidTotal, scheduledTotal) : null;

    const maxValue = Math.max(paidTotal, idealTotal, scheduledTotal);

    return [
      {
        Component: () => (
          <ProductivityTimeline
            {...{
              C,
              icon: 'scheduledTime',
              text: 'Scheduled',
              totalTime: scheduledTotal,
              maxValue,
              color: 'Selected/Light/Primary',
              active: ncFileTimes?.averageActiveSecs,
              change: ncFileTimes?.averageChangeSecs,
              setup: 0,
              borderRadiusBottom: true,
              width: 16,
              height: 16,
              withArrow: true,
            }}
          />
        ),
        key: 'ncFileScheduled',
        name: 'Scheduled',
        action: onTimePress,
      },
      {
        Component: () => (
          <ProductivityTimeline
            {...{
              C,
              icon: 'runningBunny',
              text: 'Ideal',
              totalTime: idealTotal,
              maxValue,
              color: 'Label/Light/Tertiary',
              active: ncFileTimes?.averageIdealActiveSecs,
              change: ncFileTimes?.averageIdealChangeSecs,
              setup: 0,
              width: 16,
              height: 20,
              withArrow: true,
              timeDifferenceConfig: {
                timeDifferenceInPercentage: computedIdealTimePercentage,
                lessThanTarget: idealTotal < scheduledTotal,
              },
            }}
          />
        ),
        name: 'Ideal',
        key: 'ncFileIdeal',
        action: onTimePress,
      },
      {
        Component: () => (
          <ProductivityTimeline
            {...{
              C,
              icon: 'euro',
              text: 'Paid',
              totalTime: paidTotal,
              maxValue,
              color: 'Status/Light/Paid',
              active: ncFileTimes?.averagePaidActiveSecs,
              change: ncFileTimes?.averageChangeActiveSecs,
              setup: 0,
              width: 11,
              height: 20,
              withArrow: true,
              timeDifferenceConfig: {
                timeDifferenceInPercentage: computedPaidTimePercentage,
                lessThanTarget: paidTotal < scheduledTotal,
              },
            }}
          />
        ),
        name: 'Paid',
        key: 'ncFilePaid',
        action: onTimePress,
      },
    ];
  })();

  const handleSetShouldShowGlobalFullNCFileButton = (value: boolean) => {
    setShouldShowFullNCFileButtonInternal(value);
    dispatch(setShouldShowGlobalFullNCFileButton(value));
  };

  return {
    isFocused,
    safeIsMachinesScreenCheck,
    safeIsDetailsScreen,
    isNcFilePerspective,
    batch,
    isWithERP,
    ncFileData,
    shouldShowGlobalFullNCFileButton,
    shouldShowFullNCFileButtonInternal,
    containerCoordinates,
    showFullNCFileButtonWidth,
    setShowFullNCFileButtonWidth,
    ncFileName,
    setNcFileName,
    optimisticPiecesPerCycle,
    setOptimisticPiecesPerCycle,
    scrollContainerRef,
    ncFile,
    ncFileId,
    ncFileStatus,
    clampingDetails,
    shouldProvideBatchIdDuringLinking,
    toVersions,
    versionNumber,
    ncFileStatusMessage,
    roundTopConfig,
    onNCFileNameClick,
    onNCFileImageClick,
    shouldHandleProductiveTimes,
    leftForShowFullNCFileButton,
    shadowStyles,
    productiveTimeBlockConfig,
    handleSetShouldShowGlobalFullNCFileButton,
  };
};
